import type {
  AysClinicContainer,
  BdAddress,
} from '@bindoc-projects/clinic-domain-lib';
import type { BdAdministrativeDistrictId } from '@bindoc-projects/geographic-domain-lib';

export type AysVirtualClinicReferralEntityType =
  | 'doctor'
  | 'medical-institution';

export const AYS_VIRTUAL_CLINIC_REFERRAL_ENTITY_TYPE_DISPLAY_NAME: Record<
  AysVirtualClinicReferralEntityType,
  string
> = {
  doctor: 'Ärzte',
  'medical-institution': 'Betriebsstätten',
};

export type AysVirtualClinicReferralEntityReferralType =
  | 'REFERRAL'
  | 'NON_REFERRAL';

export interface AysVirtualClinicReferralDetailsCases {
  [fabKey: string]: {
    [chapterKey: string]: {
      name: string;
      description: string;
      codes: {
        [codeName: string]: number;
      };
    };
  };
}

export interface AysVirtualClinicReferralDetails {
  additionalInfo: string;
  entity?:
    | AysVirtualClinicReferralDoctor
    | AysVirtualClinicMedicalInstitution
    | null;
  casesPerFabKey: {
    id: string;
    fabKey: string;
    clinicGroupTotal: number;
    clinicTotal: number;
    doctorClinicGroupTotalPercentage: number;
    doctorClinicTotalPercentage: number;
    doctorGroupTotal: number;
    doctorReferrersGroupTotalPercentage: number;
    referrersGroupTotal: number;
  }[];
  casesPerIcdChapter: {
    [icdChapter: string]: {
      caseCount: number;
      description: string;
      name: string;
    };
  };
  timeSeries: {
    [year: string]: {
      caseCount: number;
      revenue: number;
      casemix: number;
      cmi: number;
    };
  };
  revenuePerFabKey: {
    id: string;
    fabKey: string;
    clinicGroupTotal: number;
    clinicTotal: number;
    doctorClinicGroupTotalPercentage: number;
    doctorClinicTotalPercentage: number;
    doctorGroupTotal: number;
    doctorReferrersGroupTotalPercentage: number;
    referrersGroupTotal: number;
  }[];
}

export interface AysVirtualClinicReferralEntity {
  entity: AysVirtualClinicReferralDoctor | AysVirtualClinicMedicalInstitution;
  lifetimeAdIds: string[];
  referredCaseCount: number;
}

export interface AysVirtualClinicReferralByPostalCode {
  postalCodes: Map<string, AysVirtualClinicPostalCodeInfo>;
  otherClinics: AysClinicContainer[];
}

export interface AysVirtualClinicPostalCodeInfo {
  clinicCaseCount: number;
  entities: AysVirtualClinicReferralEntity[];
  referredCaseCount: number;
}

export interface AysVirtualClinicReferralDoctor {
  entityType: AysVirtualClinicReferralEntityType;
  type: AysVirtualClinicReferralEntityReferralType;
  lifetimeId: string;
  salutation: string;
  title: string;
  firstName: string;
  lastName: string;
  address: BdAddress;
  specialties: string[];
  schwerpunkte: string[];
  administrativeDistrictId: BdAdministrativeDistrictId;
  distance?: number;
  lifetimeAdId: string;
  medicalInstitutionId: string;
  medicalInstitutionName: string;
  medicalInstitutionNumber: string;
  subjectOfExpertise: string;
}

export interface AysVirtualClinicMedicalInstitution {
  entityType: AysVirtualClinicReferralEntityType;
  type: AysVirtualClinicReferralEntityReferralType;
  address: BdAddress;
  administrativeDistrictId: BdAdministrativeDistrictId;
  distance?: number;
  lifetimeAdIds: string[];
  lifetimeAdIdCount: number;
  medicalInstitutionId: string;
  medicalInstitutionName: string;
  medicalInstitutionNumber: string;
}

export interface AysVirtualClinicReferralOperatingNumbers {
  clinicCaseCount: number;
  referredCaseCount: number;
  numberOfNonReferrers: number;
  numberOfReferrers: number;
  casesPerFabKey: {
    id: string;
    fabKey: string;
    referredCaseCount: number;
    nonReferredCaseCount: number;
  }[];
}
